/**
 * integrityCheck.ts — Verificación automática de consistencia en el arranque.
 *
 * QUÉ HACE:
 *  Valida que las tres fuentes de datos clave del módulo Caja sean consistentes
 *  entre sí, y repara automáticamente las discrepancias detectadas:
 *
 *   Auditoría (auditoria_logs_v1)
 *       ↕  debe coincidir con
 *   Ventas (caja_ventas_historial)
 *       ↕  y con
 *   Cierres (caja_cierres_historial)
 *
 *  Si encuentra diferencias:
 *   1. Llama a reconstructMissingVentasFromAuditoria() para re-insertar ventas
 *      que están en Auditoría pero faltan del historial (ventas reconstruidas).
 *   2. Detecta sesiones abiertas sin cierre correspondiente (sesión fantasma).
 *   3. Registra exactamente qué reparó en persistenceMetrics.
 *   4. Emite el evento "altenot:integrity-repaired" para que la UI pueda notificar.
 *
 * CUÁNDO SE LLAMA:
 *  - Una sola vez al arrancar la app, en main.tsx, tras restoreFromIDBMirror()
 *    y recoverStore(). Nunca bloquea el render.
 *
 * QUÉ NO HACE:
 *  - No borra datos. Solo agrega los que faltan.
 *  - No toca datos de inventario, payroll ni tienda (fuera de alcance por ahora).
 *  - No lanza excepciones: cualquier error se registra y la app sigue.
 *
 * QUÉ EXPORTA: runIntegrityCheck(): Promise<IntegrityCheckResult>
 */

import { logger } from "@/lib/logger";
import { txRead } from "@/lib/persistence/transactionalStore";
import {
  reconstructMissingMovimientosFromAuditoria,
  reconstructMissingVentasFromAuditoria,
} from "@/features/caja/utils/reconstructCajaFromAuditoria";
import { persistenceMetrics } from "./persistenceMetrics";
import type { Log } from "@/lib/audit/types";
import type {
  MovimientoCaja,
  Venta,
  CierreCaja,
} from "@/features/caja/types/caja.types";

const AUDIT_KEY = "auditoria_logs_v1";
const VENTAS_KEY = "caja_ventas_historial";
const CIERRES_KEY = "caja_cierres_historial";
const SESIONES_KEY = "caja_sesiones_activas";
const MOVIMIENTOS_KEY = "caja_movimientos";

export interface IntegrityCheckResult {
  ok: boolean;
  ventasEnAuditoria: number;
  ventasEnHistorial: number;
  ventasReconstruidas: number;
  movimientosEnAuditoria: number;
  movimientosEnHistorial: number;
  movimientosReconstruidos: number;
  cierresEnHistorial: number;
  sesionesActivas: number;
  issues: string[];
  repairedAt: string;
}

/**
 * Ejecuta el chequeo de integridad. Idempotente: puede llamarse múltiples veces.
 * Nunca lanza: captura errores internamente.
 */
export async function runIntegrityCheck(): Promise<IntegrityCheckResult> {
  const start = Date.now();
  const issues: string[] = [];
  let ventasReconstruidas = 0;
  let movimientosReconstruidos = 0;

  const result: IntegrityCheckResult = {
    ok: true,
    ventasEnAuditoria: 0,
    ventasEnHistorial: 0,
    ventasReconstruidas: 0,
    movimientosEnAuditoria: 0,
    movimientosEnHistorial: 0,
    movimientosReconstruidos: 0,
    cierresEnHistorial: 0,
    sesionesActivas: 0,
    issues,
    repairedAt: new Date().toISOString(),
  };

  try {
    // ── 1. Leer fuentes ──────────────────────────────────────────────────────
    const auditLogs = txRead<Log[]>(AUDIT_KEY, []);
    const ventas = txRead<Venta[]>(VENTAS_KEY, []);
    const movimientos = txRead<MovimientoCaja[]>(MOVIMIENTOS_KEY, []);
    const cierres = txRead<CierreCaja[]>(CIERRES_KEY, []);
    const sesionesMap = txRead<Record<string, unknown>>(SESIONES_KEY, {});

    // ── 2. Contar entidades ──────────────────────────────────────────────────
    const SALE_ACTIONS = new Set(["caja_venta_completada", "venta_creada"]);
    const ventaLogsIds = new Set(
      auditLogs
        .filter((l) => SALE_ACTIONS.has(l.action))
        .map((l) => String(l.entidadId))
        .filter(Boolean),
    );

    const MOV_ACTIONS = new Set([
      "caja_retiro_registrado",
      "caja_gasto_registrado",
      "caja_ingreso_registrado",
      "caja_abrir_caja_registrado",
    ]);
    const movimientoLogsIds = new Set(
      auditLogs
        .filter((l) => MOV_ACTIONS.has(l.action))
        .map((l) => String(l.entidadId))
        .filter(Boolean),
    );

    const ventasEnHistorial = ventas.filter(
      (v) => v.estado === "activa" || !v.estado,
    ).length;

    result.ventasEnAuditoria = ventaLogsIds.size;
    result.ventasEnHistorial = ventasEnHistorial;
    result.movimientosEnAuditoria = movimientoLogsIds.size;
    result.movimientosEnHistorial = movimientos.length;
    result.cierresEnHistorial = cierres.length;
    result.sesionesActivas = Object.keys(sesionesMap).length;

    // ── 3. Detectar ventas faltantes ─────────────────────────────────────────
    const ventaHistorialIds = new Set(ventas.map((v) => String(v.id)));
    const ventasFaltantes = [...ventaLogsIds].filter(
      (id) => !ventaHistorialIds.has(id),
    );

    if (ventasFaltantes.length > 0) {
      issues.push(
        `${ventasFaltantes.length} venta(s) en Auditoría sin registro en historial`,
      );
      result.ok = false;

      // Reconstruir desde Auditoría (runTransaction interno)
      ventasReconstruidas = reconstructMissingVentasFromAuditoria();
      result.ventasReconstruidas = ventasReconstruidas;

      if (ventasReconstruidas > 0) {
        issues.push(
          `✓ Reconstruidas ${ventasReconstruidas} venta(s) desde Auditoría`,
        );
      }
    }

    // ── 3b. Detectar movimientos de caja faltantes ───────────────────────────
    const movimientoHistorialIds = new Set(
      movimientos.map((m) => String(m.id)),
    );
    const movimientosFaltantes = [...movimientoLogsIds].filter(
      (id) => !movimientoHistorialIds.has(id),
    );

    if (movimientosFaltantes.length > 0) {
      issues.push(
        `${movimientosFaltantes.length} movimiento(s) en Auditoría sin registro en historial`,
      );
      result.ok = false;

      movimientosReconstruidos = reconstructMissingMovimientosFromAuditoria();
      result.movimientosReconstruidos = movimientosReconstruidos;

      if (movimientosReconstruidos > 0) {
        issues.push(
          `✓ Reconstruidos ${movimientosReconstruidos} movimiento(s) desde Auditoría`,
        );
      }
    }

    // ── 4. Detectar sesiones sin cierre (sesión fantasma) ───────────────────
    const sesionIds = Object.keys(sesionesMap);
    if (sesionIds.length > 0) {
      // Una sesión "fantasma" es aquella que no tiene un cierre activo reciente.
      // Solo registramos el issue; no la cerramos automáticamente (requiere monto).
      const hoy = new Date();
      const hace24h = new Date(
        hoy.getTime() - 24 * 60 * 60 * 1000,
      ).toISOString();

      for (const pid of sesionIds) {
        const sesion = sesionesMap[pid] as Record<string, unknown> | null;
        if (!sesion) continue;
        const abiertaEn = sesion.abiertaEn as string | undefined;
        if (abiertaEn && abiertaEn < hace24h) {
          issues.push(
            `Sesión en punto ${pid} lleva abierta más de 24h (desde ${abiertaEn})`,
          );
          // No marcamos ok=false: una sesión larga puede ser legítima.
        }
      }
    }

    // ── 5. Detectar historial vacío pero cierres presentes ──────────────────
    if (ventasEnHistorial === 0 && cierres.length > 0) {
      issues.push(
        `Historial de ventas vacío pero existen ${cierres.length} cierre(s) — posible pérdida`,
      );
      result.ok = false;
    }

    // ── 6. Log y métricas ───────────────────────────────────────────────────
    const durationMs = Date.now() - start;

    if (!result.ok || ventasReconstruidas > 0 || movimientosReconstruidos > 0) {
      persistenceMetrics.recordIntegrityRepair(
        ventasReconstruidas + movimientosReconstruidos,
        {
          issues,
          durationMs,
          ventasEnAuditoria: result.ventasEnAuditoria,
          ventasEnHistorial: result.ventasEnHistorial,
          movimientosEnAuditoria: result.movimientosEnAuditoria,
          movimientosEnHistorial: result.movimientosEnHistorial,
        },
      );

      // Emitir evento para la UI (toast de soporte si lo desea)
      if (
        typeof window !== "undefined" &&
        (ventasReconstruidas > 0 || movimientosReconstruidos > 0)
      ) {
        window.dispatchEvent(
          new CustomEvent("altenot:integrity-repaired", {
            detail: { ventasReconstruidas, movimientosReconstruidos, issues },
          }),
        );
      }

      logger.warn(
        `[IntegrityCheck] ${result.ok ? "OK con reparaciones" : "PROBLEMAS DETECTADOS"} ` +
          `(${durationMs}ms):`,
        issues,
      );
    } else {
      logger.info(
        `[IntegrityCheck] OK — ${result.ventasEnHistorial} ventas, ` +
          `${result.cierresEnHistorial} cierres, ` +
          `${result.sesionesActivas} sesiones activas. (${durationMs}ms)`,
      );
    }
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    issues.push(`Error interno en integrity check: ${msg}`);
    result.ok = false;
    persistenceMetrics.recordError("integrityCheck", msg);
    logger.error("[IntegrityCheck] Error:", err);
  }

  return result;
}
